import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { X, UserPlus, Clock, User, Plus, Edit2, Trash2, Users, ArrowLeft, UserCheck, Check, ChevronRight, Pencil, BookUser } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { LIMITS } from "../../../lib/constants";
import { useHistoricoParticipantes } from "../../../hooks/useHistoricoParticipantes";
import type { Participante, ProjectCollaborator } from "../../../services/participante.service";
import { getProjectCollaborators } from "../../../services/participante.service";
import { getUserCustomization } from "../../../services/customization.service";
import type { ContatoProjeto } from "../../../services/contato.service";

type ViewMode = "menu" | "novo" | "historico" | "colaboradores" | "contatos";

interface ParticipantDrawerProps {
  open: boolean;
  editingParticipant: Participante | null;
  currentParticipants: Participante[];
  projectId: string;
  contatos: ContatoProjeto[];
  form: {
    nome: string;
    empresa: string;
    cargo: string;
    email: string;
  };
  onClose: () => void;
  onFormChange: (field: string, value: string) => void;
  onSave: () => Promise<void>;
  onBulkAdd: (nomes: string[]) => Promise<void>;
  onAddWithDetails: (data: { nome: string; empresa?: string; cargo?: string; email?: string }) => Promise<void>;
  onEdit: (participant: Participante) => void;
  onDelete: (participantId: string) => void;
  onCancelEdit: () => void;
}

export function ParticipantDrawer({
  open,
  editingParticipant,
  currentParticipants,
  projectId,
  contatos,
  form,
  onClose,
  onFormChange,
  onSave,
  onBulkAdd,
  onAddWithDetails,
  onEdit,
  onDelete,
  onCancelEdit,
}: ParticipantDrawerProps) {
  const { t } = useTranslation('common');
  const { participantes: historico, carregando, recarregar } = useHistoricoParticipantes(projectId);
  const [isSaving, setIsSaving] = useState(false);
  const [addingFromHistory, setAddingFromHistory] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>("menu");
  const [colaboradores, setColaboradores] = useState<ProjectCollaborator[]>([]);
  const [loadingColaboradores, setLoadingColaboradores] = useState(false);
  const [addingCollaborator, setAddingCollaborator] = useState<string | null>(null);
  const [defaultCompany, setDefaultCompany] = useState<string | null>(null);

  // Fetch collaborators
  const fetchColaboradores = useCallback(async () => {
    if (!projectId) return;
    setLoadingColaboradores(true);
    try {
      const data = await getProjectCollaborators(projectId);
      setColaboradores(data);
    } catch (error) {
      console.error("Erro ao buscar colaboradores:", error);
    } finally {
      setLoadingColaboradores(false);
    }
  }, [projectId]);

  // Fetch default company from customization
  const fetchDefaultCompany = useCallback(async () => {
    try {
      const customization = await getUserCustomization();
      setDefaultCompany(customization.default_company);
    } catch (error) {
      console.error("Erro ao buscar empresa padrão:", error);
    }
  }, []);

  // Reset state when drawer opens/closes
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      fetchColaboradores();
      fetchDefaultCompany();
      setViewMode("menu");
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open, fetchColaboradores, fetchDefaultCompany]);

  // Pre-fill empresa field with default company when opening for new participant
  useEffect(() => {
    if (open && !editingParticipant && defaultCompany && !form.empresa && viewMode === "novo") {
      onFormChange("empresa", defaultCompany);
    }
  }, [open, editingParticipant, defaultCompany, form.empresa, onFormChange, viewMode]);

  // Reload history when drawer opens
  useEffect(() => {
    if (open) {
      recarregar();
    }
  }, [open, recarregar]);

  // Filter out participants already in the meeting
  const currentNames = new Set(currentParticipants.map(p => p.nome.toLowerCase()));
  const availableFromHistory = historico.filter(
    nome => !currentNames.has(nome.toLowerCase())
  );

  // Check if form has changes compared to editing participant
  const hasChanges = editingParticipant
    ? form.nome !== editingParticipant.nome ||
      form.empresa !== (editingParticipant.empresa || "") ||
      form.cargo !== (editingParticipant.cargo || "") ||
      form.email !== (editingParticipant.email || "")
    : true;

  const handleSaveNew = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.nome.trim()) return;

    setIsSaving(true);
    try {
      await onSave();
      setViewMode("menu");
    } finally {
      setIsSaving(false);
    }
  };

  const handleQuickAdd = async (nome: string) => {
    setAddingFromHistory(nome);
    try {
      await onBulkAdd([nome]);
    } finally {
      setAddingFromHistory(null);
    }
  };

  // Add collaborator as participant
  const handleAddCollaborator = async (colab: ProjectCollaborator) => {
    const key = colab.email;
    setAddingCollaborator(key);
    try {
      await onAddWithDetails({
        nome: colab.nome || colab.email,
        empresa: colab.empresa || undefined,
        email: colab.email,
      });
    } finally {
      setAddingCollaborator(null);
    }
  };

  // Check if collaborator is already in meeting (by email)
  const currentEmails = new Set(
    currentParticipants
      .filter(p => p.email)
      .map(p => p.email!.toLowerCase())
  );

  const isCollaboratorInMeeting = (colab: ProjectCollaborator) => {
    return currentEmails.has(colab.email.toLowerCase());
  };

  // Count available collaborators (not yet in meeting)
  const availableColaboradores = colaboradores.filter(c => !isCollaboratorInMeeting(c));

  // Check if contact is already in meeting (by email or name)
  const isContactInMeeting = (contato: ContatoProjeto) => {
    if (contato.email) {
      return currentEmails.has(contato.email.toLowerCase());
    }
    return currentNames.has(contato.nome.toLowerCase());
  };

  // Count available contacts (not yet in meeting)
  const availableContatos = contatos.filter(c => !isContactInMeeting(c));

  const [addingContato, setAddingContato] = useState<number | null>(null);

  const handleAddContato = async (contato: ContatoProjeto) => {
    setAddingContato(contato.id);
    try {
      await onAddWithDetails({
        nome: contato.nome,
        empresa: contato.empresa || undefined,
        cargo: contato.cargo || undefined,
        email: contato.email || undefined,
      });
    } finally {
      setAddingContato(null);
    }
  };

  const handleBackToMenu = () => {
    setViewMode("menu");
    onCancelEdit();
  };

  if (!open) return null;

  // Determine the title based on view mode
  const getTitle = () => {
    if (editingParticipant) return t('participantDrawer.edit_participant');
    switch (viewMode) {
      case "novo": return t('participantDrawer.create_new');
      case "historico": return t('participantDrawer.select_from_history');
      case "colaboradores": return t('participantDrawer.add_collaborator');
      case "contatos": return t('participantDrawer.add_from_contacts');
      default: return t('participantDrawer.title');
    }
  };

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 z-60 fade-in"
        style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
        onClick={onClose}
      />

      {/* Drawer */}
      <aside
        className="w-full md:w-[420px] border-l border-border flex flex-col bg-background fixed right-0 top-0 bottom-0 z-70"
        style={{
          height: "100vh",
          animation: "slideInRight 300ms cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        {/* Header */}
        <div className="px-4 py-3 border-b border-border flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2">
            {(viewMode !== "menu" || editingParticipant) && (
              <button
                onClick={handleBackToMenu}
                className="p-1.5 hover:bg-muted rounded-md transition-colors -ml-1.5"
                aria-label={t('participantDrawer.back')}
              >
                <ArrowLeft size={18} className="text-muted-foreground" />
              </button>
            )}
            {viewMode === "menu" && !editingParticipant && (
              <Users size={18} className="text-primary" />
            )}
            <h3 className="font-semibold text-foreground">{getTitle()}</h3>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-muted rounded-md transition-colors"
            aria-label={t('participantDrawer.close')}
          >
            <X size={18} className="text-muted-foreground" />
          </button>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto">

          {/* ===== MENU VIEW ===== */}
          {viewMode === "menu" && !editingParticipant && (
            <>
              {/* Add Options Section */}
              <div className="p-4 border-b border-border">
                <p className="text-sm font-medium text-foreground mb-3">
                  {t('participantDrawer.add_participant')}
                </p>

                <div className="space-y-2">
                  {/* Option 1: Create New */}
                  <button
                    onClick={() => setViewMode("novo")}
                    className="w-full flex items-center gap-3 p-3 rounded-lg border border-border bg-background hover:bg-muted/50 hover:border-muted-foreground/30 transition-colors text-left"
                  >
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Pencil size={18} className="text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{t('participantDrawer.create_new')}</p>
                      <p className="text-xs text-muted-foreground">
                        {t('participantDrawer.create_new_description')}
                      </p>
                    </div>
                    <ChevronRight size={18} className="text-muted-foreground" />
                  </button>

                  {/* Option 2: From History */}
                  <button
                    onClick={() => setViewMode("historico")}
                    className="w-full flex items-center gap-3 p-3 rounded-lg border border-border bg-background hover:bg-muted/50 hover:border-muted-foreground/30 transition-colors text-left"
                  >
                    <div className="w-10 h-10 rounded-full bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                      <Clock size={18} className="text-amber-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{t('participantDrawer.select_from_history')}</p>
                      <p className="text-xs text-muted-foreground">
                        {t('participantDrawer.history_description')}
                      </p>
                    </div>
                    {availableFromHistory.length > 0 && (
                      <span className="text-xs font-medium bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                        {availableFromHistory.length}
                      </span>
                    )}
                    <ChevronRight size={18} className="text-muted-foreground" />
                  </button>

                  {/* Option 3: From Collaborators */}
                  <button
                    onClick={() => setViewMode("colaboradores")}
                    className="w-full flex items-center gap-3 p-3 rounded-lg border border-border bg-background hover:bg-muted/50 hover:border-muted-foreground/30 transition-colors text-left"
                  >
                    <div className="w-10 h-10 rounded-full bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
                      <UserCheck size={18} className="text-emerald-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{t('participantDrawer.add_collaborator')}</p>
                      <p className="text-xs text-muted-foreground">
                        {t('participantDrawer.collaborators_description')}
                      </p>
                    </div>
                    {availableColaboradores.length > 0 && (
                      <span className="text-xs font-medium bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">
                        {availableColaboradores.length}
                      </span>
                    )}
                    <ChevronRight size={18} className="text-muted-foreground" />
                  </button>

                  {/* Option 4: From Contacts */}
                  <button
                    onClick={() => setViewMode("contatos")}
                    className="w-full flex items-center gap-3 p-3 rounded-lg border border-border bg-background hover:bg-muted/50 hover:border-muted-foreground/30 transition-colors text-left"
                  >
                    <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                      <BookUser size={18} className="text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{t('participantDrawer.add_from_contacts')}</p>
                      <p className="text-xs text-muted-foreground">
                        {t('participantDrawer.contacts_description')}
                      </p>
                    </div>
                    {availableContatos.length > 0 && (
                      <span className="text-xs font-medium bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                        {availableContatos.length}
                      </span>
                    )}
                    <ChevronRight size={18} className="text-muted-foreground" />
                  </button>
                </div>
              </div>

              {/* Current Participants */}
              <div className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <User size={14} className="text-muted-foreground" />
                  <span className="text-sm font-medium text-muted-foreground">
                    {t('participantDrawer.in_this_meeting')}
                  </span>
                  <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                    {currentParticipants.length}
                  </span>
                </div>

                {currentParticipants.length === 0 ? (
                  <div className="text-center py-8">
                    <User className="h-10 w-10 text-muted-foreground/20 mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">{t('participantDrawer.no_participants_added')}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {t('participantDrawer.choose_option_above')}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {currentParticipants.map((p) => (
                      <div
                        key={p.id}
                        className="group flex items-center gap-3 p-3 bg-background border border-border rounded-lg hover:bg-muted/50 transition-colors"
                      >
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-medium text-primary">
                            {p.nome.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{p.nome}</p>
                          {(p.empresa || p.cargo) && (
                            <p className="text-xs text-muted-foreground truncate">
                              {p.empresa}
                              {p.empresa && p.cargo && " · "}
                              {p.cargo}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => onEdit(p)}
                            className="p-1.5 hover:bg-muted rounded transition-colors"
                            title="Editar"
                          >
                            <Edit2 size={14} className="text-muted-foreground" />
                          </button>
                          <button
                            onClick={() => onDelete(p.id)}
                            className="p-1.5 hover:bg-destructive/10 rounded transition-colors"
                            title="Excluir"
                          >
                            <Trash2 size={14} className="text-destructive" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          {/* ===== CREATE NEW VIEW ===== */}
          {(viewMode === "novo" || editingParticipant) && (
            <div className="p-4">
              <form onSubmit={handleSaveNew}>
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-foreground mb-1.5 block">
                      {t('participantDrawer.name_required')}
                    </label>
                    <Input
                      type="text"
                      value={form.nome}
                      onChange={(e) => onFormChange("nome", e.target.value)}
                      maxLength={LIMITS.PARTICIPANT_NAME_MAX}
                      placeholder={t('participantDrawer.name_placeholder')}
                      autoFocus
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-foreground mb-1.5 block">
                      {t('participantDrawer.company')}
                    </label>
                    <Input
                      type="text"
                      value={form.empresa}
                      onChange={(e) => onFormChange("empresa", e.target.value)}
                      maxLength={LIMITS.PARTICIPANT_COMPANY_MAX}
                      placeholder={t('participantDrawer.company_placeholder')}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-sm font-medium text-foreground mb-1.5 block">
                        {t('participantDrawer.role')}
                      </label>
                      <Input
                        type="text"
                        value={form.cargo}
                        onChange={(e) => onFormChange("cargo", e.target.value)}
                        maxLength={LIMITS.PARTICIPANT_ROLE_MAX}
                        placeholder={t('participantDrawer.role_placeholder')}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-foreground mb-1.5 block">
                        {t('participantDrawer.email')}
                      </label>
                      <Input
                        type="email"
                        value={form.email}
                        onChange={(e) => onFormChange("email", e.target.value)}
                        maxLength={LIMITS.PARTICIPANT_EMAIL_MAX}
                        placeholder={t('participantDrawer.email_placeholder')}
                      />
                    </div>
                  </div>

                  <div className="flex gap-2 pt-2">
                    <Button
                      type="button"
                      variant="outline"
                      className="flex-1"
                      onClick={handleBackToMenu}
                    >
                      {t('cancel')}
                    </Button>
                    <Button
                      type="submit"
                      className="flex-1"
                      disabled={isSaving || !form.nome.trim() || (!!editingParticipant && !hasChanges)}
                    >
                      {isSaving ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      ) : editingParticipant ? (
                        t('participantDrawer.save_changes')
                      ) : (
                        t('participantDrawer.add_button')
                      )}
                    </Button>
                  </div>
                </div>
              </form>
            </div>
          )}

          {/* ===== HISTORY VIEW ===== */}
          {viewMode === "historico" && !editingParticipant && (
            <div className="p-4">
              {carregando ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                </div>
              ) : availableFromHistory.length === 0 ? (
                <div className="text-center py-12">
                  <Clock className="h-12 w-12 text-muted-foreground/20 mx-auto mb-3" />
                  <p className="text-sm font-medium text-foreground">
                    {historico.length > 0
                      ? t('participantDrawer.all_already_added')
                      : t('participantDrawer.no_history_yet')}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {historico.length > 0
                      ? t('participantDrawer.all_from_history_added')
                      : t('participantDrawer.participants_will_appear')}
                  </p>
                </div>
              ) : (
                <>
                  <p className="text-xs text-muted-foreground mb-3">
                    {t('participantDrawer.click_to_add')}
                  </p>
                  <div className="space-y-2">
                    {availableFromHistory.map((nome, index) => (
                      <button
                        key={`${nome}-${index}`}
                        onClick={() => handleQuickAdd(nome)}
                        disabled={addingFromHistory === nome}
                        className="w-full flex items-center gap-3 p-3 rounded-lg border border-border bg-background hover:bg-muted/50 hover:border-muted-foreground/30 transition-colors text-left disabled:opacity-50"
                      >
                        <div className="w-8 h-8 rounded-full bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-medium text-amber-600">
                            {nome.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{nome}</p>
                        </div>
                        {addingFromHistory === nome ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                        ) : (
                          <Plus size={18} className="text-muted-foreground" />
                        )}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* ===== COLLABORATORS VIEW ===== */}
          {viewMode === "colaboradores" && !editingParticipant && (
            <div className="p-4">
              {loadingColaboradores ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                </div>
              ) : colaboradores.length === 0 ? (
                <div className="text-center py-12">
                  <UserCheck className="h-12 w-12 text-muted-foreground/20 mx-auto mb-3" />
                  <p className="text-sm font-medium text-foreground">
                    {t('participantDrawer.no_collaborators_in_project')}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('participantDrawer.invite_people_to_collaborate')}
                  </p>
                </div>
              ) : (
                <>
                  <p className="text-xs text-muted-foreground mb-3">
                    {t('participantDrawer.click_to_add')}
                  </p>
                  <div className="space-y-2">
                    {colaboradores.map((colab) => {
                      const inMeeting = isCollaboratorInMeeting(colab);
                      const isAdding = addingCollaborator === colab.email;
                      return (
                        <button
                          key={colab.email}
                          onClick={() => !inMeeting && handleAddCollaborator(colab)}
                          disabled={inMeeting || isAdding}
                          className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-colors text-left ${
                            inMeeting
                              ? "bg-emerald-50 border-emerald-200 cursor-default"
                              : "bg-background border-border hover:bg-muted/50 hover:border-muted-foreground/30 disabled:opacity-50"
                          }`}
                        >
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                            inMeeting ? "bg-emerald-100" : "bg-emerald-500/10"
                          }`}>
                            <span className={`text-xs font-medium ${
                              inMeeting ? "text-emerald-700" : "text-emerald-600"
                            }`}>
                              {(colab.nome || colab.email).charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">
                              {colab.nome || colab.email}
                            </p>
                            <p className="text-xs text-muted-foreground truncate">
                              {colab.email}
                              {colab.empresa && ` · ${colab.empresa}`}
                            </p>
                          </div>
                          {inMeeting ? (
                            <div className="flex items-center gap-1 text-xs text-emerald-600">
                              <Check size={14} />
                              <span>{t('participantDrawer.added')}</span>
                            </div>
                          ) : isAdding ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                          ) : (
                            <Plus size={18} className="text-muted-foreground" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}

          {/* ===== CONTACTS VIEW ===== */}
          {viewMode === "contatos" && !editingParticipant && (
            <div className="p-4">
              {contatos.length === 0 ? (
                <div className="text-center py-12">
                  <BookUser className="h-12 w-12 text-muted-foreground/20 mx-auto mb-3" />
                  <p className="text-sm font-medium text-foreground">
                    {t('participantDrawer.no_contacts_in_project')}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('participantDrawer.add_contacts_in_project')}
                  </p>
                </div>
              ) : (
                <>
                  <p className="text-xs text-muted-foreground mb-3">
                    {t('participantDrawer.click_to_add')}
                  </p>
                  <div className="space-y-2">
                    {contatos.map((contato) => {
                      const inMeeting = isContactInMeeting(contato);
                      const isAdding = addingContato === contato.id;
                      return (
                        <button
                          key={contato.id}
                          onClick={() => !inMeeting && handleAddContato(contato)}
                          disabled={inMeeting || isAdding}
                          className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-colors text-left ${
                            inMeeting
                              ? "bg-blue-50 border-blue-200 cursor-default"
                              : "bg-background border-border hover:bg-muted/50 hover:border-muted-foreground/30 disabled:opacity-50"
                          }`}
                        >
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                            inMeeting ? "bg-blue-100" : "bg-blue-500/10"
                          }`}>
                            <span className={`text-xs font-medium ${
                              inMeeting ? "text-blue-700" : "text-blue-600"
                            }`}>
                              {contato.nome.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{contato.nome}</p>
                            <p className="text-xs text-muted-foreground truncate">
                              {[contato.cargo, contato.empresa].filter(Boolean).join(" · ")}
                            </p>
                          </div>
                          {inMeeting ? (
                            <div className="flex items-center gap-1 text-xs text-blue-600">
                              <Check size={14} />
                              <span>{t('participantDrawer.added')}</span>
                            </div>
                          ) : isAdding ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                          ) : (
                            <Plus size={18} className="text-muted-foreground" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </aside>
    </>
  );
}
